import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {NewBusinessAccount} from './new-business-account';
import {UntypedFormControl, NgForm, NgModel} from '@angular/forms';
import {IdAndLabel} from '../common/model/id-and-label';
import {EnumsService} from '../common/services/enums.service';
import {AppAvailableLanguagesService} from '../../app-available-languages.service';
import {TranslateService} from '@ngx-translate/core';
import {CountryType, ErrorType, LanguageType} from '../common/enums/enums';
import {Localization} from "../common/enums/localization";
import {ModalDirective} from 'ng-uikit-pro-standard';
import {BusinessRulesService} from '../../services/business-rules.service';
import {RegisterAccountService} from './register-account.service';
import {AccountSiteService} from '../common/services/account-site.service';
import {Observable, Subject} from 'rxjs';
import {camelCase} from 'lodash-es';
import {AppLanguageService} from '../../i18n/app.language.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  providers: [RegisterAccountService]
})
export class RegisterComponent implements OnInit, AfterViewInit {

  @ViewChild('firstName') firstNameControl: UntypedFormControl;
  @ViewChild('lastName') lastNameControl: UntypedFormControl;
  @ViewChild('company') companyControl: UntypedFormControl;
  @ViewChild('email') emailControl: NgModel;
  @ViewChild('password') passwordControl: UntypedFormControl;
  @ViewChild('cPassword') cPasswordControl: UntypedFormControl;
  @ViewChild('modal') modal: ModalDirective;
  @ViewChild('registerForm', { static: true }) registerForm: NgForm;

  public model: NewBusinessAccount;
  public countries$: Observable<IdAndLabel[]>;
  public countriesAndStates: any;
  public statesLabel: string;
  public states: IdAndLabel[];
  public langs$: Observable<IdAndLabel[]>;
  public confirmPassword: string;
  public doPasswordsMatch: boolean;
  public isRegisterInProgress: boolean;
  public accountCreated: boolean = false;
  public supportedLangs: string[];
  public emailInputErrorMsg: string;
  public readonly Localization = Localization;
  public registeredEmail: string;

  private availableLanguagesSubject = new Subject<any>();

  constructor(
    private enumsService: EnumsService,
    private availableLangsService: AppAvailableLanguagesService,
    private translate: TranslateService,
    private businessRulesService: BusinessRulesService,
    private registerAccountService: RegisterAccountService,
    private accountSiteService: AccountSiteService,
    private appLanguageService: AppLanguageService) { }

  ngOnInit() {
    this.countriesAndStates = {};
    this.statesLabel = '';
    this.states = [];
    this.isRegisterInProgress = false;
    this.resetModel();
    this.countries$ = this.enumsService.enumAsOptions$(CountryType, 'countryType');
    this.getCountriesAndStates();

    this.availableLangsService.getLanguages().subscribe(
      langs => {
        this.supportedLangs = langs.slice(0);
        this.availableLanguagesSubject.next(this.supportedLangs);
        this.langs$ = this.enumsService.enumAsOptions$(this.convertArrayToObject(langs), 'languageType');
        this.setLanguageAsEnglishIfBrowserLanguageIsNotSupported(langs);
      }
    );

    if (this.model.country != null) {
      this.statesLabel = this.getStatesLabel(this.model.country);
      this.states = this.getStatesByCountry(this.model.country);
    } else {
      this.statesLabel = '';
      this.states = [];
    }

    this.changeLocalization();
  }

  ngAfterViewInit() {
    this.initFormControls();
  }

  public onPasswordChange() {
    if (this.model.password === this.confirmPassword) {
      this.doPasswordsMatch = true;
    } else {
      this.doPasswordsMatch = false;
    }
  }

  public onCountryChange(event: any) {
    this.changeLocalization();
    this.model.state = null;
    this.statesLabel = this.getStatesLabel(event.id);
    this.states = this.getStatesByCountry(event.id);
  }

  public doesSelectedCountryHaveStates(): boolean {
    return this.states.length > 0;
  }

  public onLanguageChange() {
    this.changeLocalization();
  }

  public isFormValid(): boolean {
    return this.registerForm && this.registerForm.valid && this.doPasswordsMatch;
  }

  public register() {
    this.isRegisterInProgress = true;
    this.registerAccountService.registerAccount(this.model).subscribe(
      response => {
        if (response === ErrorType.OK) {
          this.accountCreated = true;
          this.registeredEmail = this.model.username;
          this.resetControls();
        } else if (response === ErrorType.ALREADYEXISTS) {
          this.flagEmailAsTaken();
        }
      },
      e => {
        this.isRegisterInProgress = false;
      },
      () => this.isRegisterInProgress = false,
    );
  }

  public flagEmailAsTaken() {
    this.emailControl.control.setErrors({ 'taken': true });
    this.emailInputErrorMsg = this.getErrorMessageByErrorType(this.emailControl.control);
  }

  public goToAccountLoginPage() {
    this.accountSiteService.goToLoginPage(true);
  }

  private convertArrayToObject(arr: string[]): any {
    const obj = {};
    for (let i = 0; i < arr.length; ++i) {
      obj[i] = arr[i];
    }
    return obj;
  }

  private resetControls() {
    this.firstNameControl.reset();
    this.lastNameControl.reset();
    this.companyControl.reset();
    this.emailControl.reset();
    this.passwordControl.reset();
    this.cPasswordControl.reset();
  }

  private resetModel() {
    this.model = new NewBusinessAccount();
    this.confirmPassword = '';
    this.doPasswordsMatch = true;
    this.accountCreated = false;
    this.setStartingLanguageAndCountry();
  }

  private setLanguageAsEnglishIfBrowserLanguageIsNotSupported(langs: string[]) {
    if (langs == null || !langs.includes(this.model.language)) {
      this.model.language = LanguageType.EN;
      this.changeLocalization();
    }
  }

  private changeLocalization() {
    let culture = this.model.language.toLowerCase();
    if (this.model.country)
      culture += `-${this.model.country}`;
    this.translate.use(culture);
  }

  private setStartingLanguageAndCountry() {
    this.appLanguageService.getStartingLanguage().subscribe(c => {
      const a = c.split('-');
      this.model.language = a[0].toUpperCase() as LanguageType;
      if (a[1]) {
        this.model.country = a[1] as CountryType;
      }
    });
  }

  private initFormControls() {
    this.emailInputErrorMsg = '';
    this.emailControl.control.valueChanges
      .subscribe(() => this.emailInputErrorMsg = this.getErrorMessageByErrorType(this.emailControl.control));
  }

  private getErrorMessageByErrorType(control: UntypedFormControl): string {
    let result = '';
    if (control.hasError('email')) {
      result = Localization.EmailIsIncorrect;
    } else if (control.hasError('required')) {
      result = Localization.Required;
    } else if (control.hasError('taken')) {
      result = this.Localization.UsernameAlreadyExists;
    }
    return result;
  }

  private getCountriesAndStates() {
    this.countriesAndStates = this.businessRulesService.getCountriesAndStates();
  }

  private getStatesLabel(country: CountryType): string {
    return this.translate.instant(country == CountryType.CA ? Localization.Province : Localization.State);
  }

  private getStatesByCountry(country: CountryType): IdAndLabel[] {
    let result = [];
    let countryProp = camelCase(country);
    if (this.countriesAndStates.hasOwnProperty(countryProp) && Array.isArray(this.countriesAndStates[countryProp])) {
      result = this.countriesAndStates[countryProp].map(item => new IdAndLabel(item, this.translate.instant('stateType_' + item)));
    }
    return result;
  }
}
